import React, { useState, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash } from "@fortawesome/free-solid-svg-icons"
import { useSelector } from "react-redux"
import { selectUser } from "../../../../redux/store/auth/authSlice"
import { Meeting } from "../../../../types/brandDatas"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import TimePicker from "react-time-picker"
import "react-time-picker/dist/TimePicker.css"

interface MeetingModalProps {
  isOpen: boolean
  onClose: () => void
  brandId: string
  meetings: Meeting[]
  onAddMeeting: (brandId: string, meeting: { time: Date; description: string }) => Promise<void>
  onDeleteMeeting: (brandId: string, index: number) => void
}

const MeetingModal: React.FC<MeetingModalProps> = ({
  isOpen,
  onClose,
  brandId,
  meetings,
  onAddMeeting,
  onDeleteMeeting,
}) => {
  const [localMeetings, setLocalMeetings] = useState<Meeting[]>([])
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [meetingDescription, setMeetingDescription] = useState("")
  const [isSaving, setIsSaving] = useState(false)

  const userEmail = useSelector(selectUser)?.replace(/"/g, "") || ""

  useEffect(() => {
    if (isOpen) {
      setLocalMeetings(meetings || [])
      setMeetingDescription("")
      setSelectedDate(new Date())
    }
  }, [isOpen, meetings])

  const handleDateChange = (date: any) => {
    setSelectedDate(date)
  }

  const handleTimeChange = (time: any) => {
    if (time !== null) {
      const newDate = new Date(selectedDate)
      const [hours, minutes] = time.split(":")
      newDate.setHours(hours)
      newDate.setMinutes(minutes)
      setSelectedDate(newDate)
    }
  }

  const handleAddMeeting = async () => {
    if (!meetingDescription.trim()) {
      return
    }

    const newMeeting: Meeting = {
      time: selectedDate,
      description: meetingDescription.trim(),
      author: userEmail,
    }

    setLocalMeetings((prev) => [newMeeting, ...prev])

    setIsSaving(true)
    try {
      await onAddMeeting(brandId, { time: selectedDate, description: meetingDescription.trim() })
      setMeetingDescription("")
      setSelectedDate(new Date())
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleAddMeeting()
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed right-0 top-0 h-full z-50">
      <div className="bg-white h-full w-96 shadow-lg flex flex-col">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold">Toplantılar</h2>
              <p className="text-sm opacity-90 mt-1">Marka ID: {brandId}</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 p-4 overflow-y-auto">
          <div className="mb-6">
            {localMeetings && localMeetings.length > 0 ? (
              <div className="space-y-3">
                {localMeetings.map((meeting, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <p className="text-gray-800 font-medium text-sm">
                            {new Date(meeting.time).toLocaleString("tr-TR")}
                          </p>
                        </div>
                        <p className="text-gray-600 text-sm mb-2">{meeting.description}</p>
                        <div className="flex items-center text-xs text-gray-500">
                          <span className="font-semibold">Ekleyen:</span>
                          <span className="ml-1">{meeting.author}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          onDeleteMeeting(brandId, index)
                          setLocalMeetings((prev) => prev.filter((_, i) => i !== index))
                        }}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>Henüz toplantı eklenmemiş</p>
              </div>
            )}
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Yeni Toplantı Ekle</h3>
            <div className="flex flex-col gap-3">
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">Tarih</label>
                  <DatePicker
                    selected={selectedDate}
                    onChange={handleDateChange}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholderText="Tarih seçin"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">Saat</label>
                  <TimePicker
                    onChange={handleTimeChange}
                    value={
                      selectedDate
                        ? selectedDate.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""
                    }
                    className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disableClock={true}
                    clearIcon={null}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">Açıklama</label>
                <textarea
                  value={meetingDescription}
                  onChange={(e) => setMeetingDescription(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Toplantı açıklamasını buraya yazın..."
                  className="w-full p-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  rows={3}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border-t p-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Toplam: {localMeetings?.length || 0} toplantı
            </div>
            <button
              onClick={handleAddMeeting}
              disabled={!meetingDescription.trim() || isSaving}
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>{isSaving ? "Ekleniyor..." : "Ekle"}</span>
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MeetingModal
